<template>
  <div ref="rightSide" class="flex flex-col gap-5 lg:gap-6">
    <div
      class="max-lg:visually-hidden flex items-start justify-between gap-2.5"
    >
      <h1 class="text-xl font-light !tracking-[0.2px] text-white">
        {{ productTitle }}
      </h1>
    </div>
    <div class="space-y-3.5 border-t border-off-white-30 max-lg:-mx-4">
      <UiExpandedBlock
        v-for="(filters, key, index) in groupedFiltersByTabs"
        :key="key"
        :model-value="true"
        content-classes="mt-0"
      >
        <template #activator="{ attrs, open }">
          <button
            v-bind="attrs"
            class="flex w-full items-center justify-between bg-off-white-30 p-4 text-sm uppercase text-red-80"
          >
            <span class="text-left">
              <span
                class="mr-2.5 inline-block bg-white px-4 py-1 font-bold text-primary"
              >
                {{ index + 1 }}
              </span>
              <span class="font-light">{{ tabsTitle[key] }}</span>
            </span>
            <UiIcon
              :name="open ? 'chevron_up' : 'chevron_down'"
              class="pointer-events-none size-6 flex-shrink-0 text-primary"
            />
          </button>
        </template>
        <template #content>
          <div class="max-lg:px-4">
            <div v-for="block in filters" :key="block?.id">
              <ProductMaterialFilter
                :result="result"
                :additional-options="additionalOptions"
                :block="block"
                label-class="uppercase font-light text-xs"
                hide-learn-more-link
                show-if-one-property-in-list
                @filter="onFilterClick"
                @options-filter="onOptionsFilterClick"
              />
            </div>
          </div>
        </template>
      </UiExpandedBlock>
    </div>

    <div
      v-if="currentConfiguration?.data"
      class="flex justify-center bg-off-white-30 p-4 max-lg:-mx-4 max-lg:flex-col lg:items-center"
    >
      <div
        class="mr-5 flex items-center gap-2.5 pb-2.5 text-xs font-light uppercase lg:py-1.5"
      >
        <template v-if="isOutOfStock">
          {{ $t("outOfStock") }}
        </template>
        <template v-else>
          {{ $t("totalPrice") }}:
          <span v-if="productStartAtPrice" class="text-white line-through mr-1">
            {{ $n(productStartAtPrice, "currency") }}
          </span>
          <span class="text-2xl font-bold text-orange-50">{{
            $n(finalProductPrice, "currency")
          }}</span>
        </template>
      </div>
      <div
        v-if="!isOutOfStock"
        class="border-gray-70 pt-2.5 max-lg:w-full max-lg:border-t lg:border-l lg:py-1.5 lg:pl-5"
      >
        <ProductAffirmBlock />
      </div>
    </div>
    <section v-if="!isOutOfStock" class="add-to-bag-section relative pb-4">
      <div class="add-to-bag-btn">
        <UiButton
          :loading="addToCartLoading"
          class="w-full uppercase"
          size="lg"
          :disabled="addToCartLoading || hasUnavailableMaterial"
          @click="onAddToCart"
        >
          {{ $t("finalizeYourRing") }}
          <UiIcon name="bag" class="ml-2 size-6" />
        </UiButton>
      </div>
      <SharedFieldHint
        v-if="hasUnavailableMaterial"
        class="mt-4"
        :message="$t('unavailableConfiguration')"
        error
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import {
  FilteredMaterialsByCategoriesI,
  IProductMaterialFilterEvent,
  IProductPdpInfoProps,
} from "ecom-types";
import ProductMaterialFilter from "~/components/product/ProductMaterialFilter.vue";
import { propertiesGroupSkus, propertiesGroupSlugs } from "~/configs";

const props = defineProps<IProductPdpInfoProps>();

const emit = defineEmits([
  "addToCart",
  "optionsFilter",
  "filter",
  "updateWishlist",
]);

const { t } = useI18n();

const tabs = {
  shape: [propertiesGroupSkus.shape, propertiesGroupSkus.stoneSize],
  settings: [
    propertiesGroupSkus.style,
    propertiesGroupSkus.designCategory,
    propertiesGroupSkus.metalColor,
    propertiesGroupSkus.metalCaratWeight,
    propertiesGroupSkus.ringSize,
  ],
  stone: [propertiesGroupSkus.stoneType],
};

const propertiesGroupOverwrites = {
  [propertiesGroupSkus.shape]: {
    title: "",
    view_type: filterViewType.icon,
    sort_order: 1,
  },
  [propertiesGroupSkus.stoneSize]: {
    title: t("centerStoneCaratWeight"),
    view_type: filterViewType.button,
    sort_order: 2,
  },

  [propertiesGroupSkus.designCategory]: {
    title: t("style"),
    view_type: filterViewType.button_big,
    sort_order: 3,
  },
  [propertiesGroupSkus.style]: {
    title: t("design"),
    view_type: filterViewType.icon,
    sort_order: 4,
  },
  [propertiesGroupSkus.metalColor]: {
    title: t("chooseYourMetal"),
    view_type: filterViewType.icon,
    sort_order: 5,
  },
  [propertiesGroupSkus.metalCaratWeight]: {
    title: t("metalCaratWeight"),
    view_type: filterViewType.button_big,
    sort_order: 6,
  },
  [propertiesGroupSkus.ringSize]: {
    title: t("selectYourRingSize"),
    view_type: filterViewType.button,
    sort_order: 7,
  },
  [propertiesGroupSkus.stoneType]: {
    title: "",
    view_type: filterViewType.checkbox,
    sort_order: 8,
  },
};

type TabKeys = keyof typeof tabs;

const tabsTitle = computed<Record<TabKeys, string>>(() => {
  return {
    shape: t("chooseYourShape"),
    settings: t("chooseYourSetting"),
    stone: t("chooseYourStoneType"),
  };
});

const designCategoryResult = computed(() => {
  return (
    props.result[propertiesGroupSlugs.designCategory] ||
    props.additionalOptions[propertiesGroupSlugs.designCategory]
  );
});

const overwrittenCategories = computed(() => {
  const categories = JSON.parse(
    JSON.stringify(props.filteredMaterialsByCategories),
  );
  return categories.map((category) => {
    const overwrittenCategory = propertiesGroupOverwrites[category.sku];
    if (overwrittenCategory) {
      category.view_type = overwrittenCategory.view_type;
      category.sort_order =
        overwrittenCategory.sort_order || category.sort_order;
      category.currentTranslation.title = overwrittenCategory.title;

      if (
        category.sku === propertiesGroupSkus.style &&
        designCategoryResult.value
      ) {
        if (designCategoryResult.value.tags?.length) {
          category.materials = category.materials.filter((material) =>
            designCategoryResult.value?.tags?.some((tag) => {
              return tag.currentTranslation.name === material.sku;
            }),
          );
        }
      }
    }
    return category;
  });
});

const groupedFiltersByTabs = computed(() => {
  const result = overwrittenCategories.value.reduce(
    (acc, category) => {
      Object.keys(tabs).forEach((tab) => {
        if (tabs[tab].includes(category.sku)) {
          acc[tab].push(category);
        }
      });
      return acc;
    },
    {
      shape: [],
      settings: [],
      stone: [],
    } as Record<TabKeys, FilteredMaterialsByCategoriesI[]>,
  );

  // sort items within each category
  result.shape.sort((a, b) => a.sort_order - b.sort_order);
  result.settings.sort((a, b) => a.sort_order - b.sort_order);
  result.stone.sort((a, b) => a.sort_order - b.sort_order);

  return result;
});

function onAddToCart() {
  if (props.hasUnavailableMaterial) return;
  emit("addToCart");
}

function onOptionsFilterClick(value: IProductMaterialFilterEvent) {
  emit("optionsFilter", value);
}

function onFilterClick(value: IProductMaterialFilterEvent) {
  emit("filter", value);
}
</script>
